import {
  IFilterDetails,
  IFilterFacet,
  SortDirection,
} from "../interfaces/interfaces";
import {
  customFilterOptions,
  genericFilterOptions,
  groupByFilterOptions,
  ontologyFilterOptions,
} from "./filterOptions";
import { validateFilterFacets } from "./filterConfigValidator";

function getFacetIdentifier(facet: IFilterFacet): string {
  if (facet.facetIdentifier?.trim()) {
    return facet.facetIdentifier.trim().replaceAll(" ", "");
  }

  return facet.facetTitle
    ? facet.facetTitle.replaceAll(" ", "")
    : facet.applyToColumn || "";
}

export const filterTemplate = {
  facetIdentifier: "NewFilter",
  facetTitle: "New Filter",
  component: "CheckboxFilter",
  sourceTable: "",
  applyToColumn: "",
  filterValueAttribute: "id",
  filterLabelAttribute: "name",
  removeOptions: [],
  customOptions:
    [] /** an array with objects that substitute calling the database for options, { text: labelToShow, value: valueToFilterOn } */,
  ontologyIdentifiers: [],
  trueOption: undefined /** use this in combination with a toggle filter */,
  showMatchTypeSelector: true,
  negotiatorRequestString: "New Filter:",
  adaptive: false,
  sortColumn: "name",
  sortDirection: "asc",
  showFacet: true,
};

export function createFilters(
  filterFacets: IFilterFacet[]
): Record<string, IFilterDetails> {
  const filterDetails: Record<string, IFilterDetails> = {};

  for (const facet of filterFacets) {
    const facetId = getFacetIdentifier(facet);
    filterDetails[facetId] = {
      facetTitle:
        facet.facetTitle ||
        facet.applyToColumn ||
        "invalidFacetTitle" /** a custom 'human readable' text for on the button */,
      facetIdentifier: facetId,
      component:
        facet.component ||
        "CheckboxFilter" /** a custom specified component, or just the default */,
      sourceTable:
        facet.sourceTable /** the table where the options are coming from. */,
      applyToColumn:
        facet.applyToColumn /** the column in the main table to apply the filter on. */,
      filterValueAttribute:
        facet.filterValueAttribute ||
        "id" /** specify a column name if you want a different column for the value */,
      filterLabelAttribute:
        facet.filterLabelAttribute ||
        "name" /** specify if you want to use another column as the label for the filter option, instead of name */,
      options: getFilterOptions({
        ...facet,
        facetIdentifier: facetId,
      }),
      ontologyIdentifiers:
        facet.ontologyIdentifiers ||
        [] /** for use when you have multiple ontologies in a single table, e.g. orhpa and icd */,
      optionsFromGroupBy:
        facet.optionsFromGroupBy ||
        false /** fetch options as distinct values from the main table instead of a lookup table */,
      trueOption: facet.trueOption /** use this for a togglefilter */,
      matchTypeForFilter:
        "any" /** if it has been selected from bookmark, it will be applied here. */,
      showMatchTypeSelector: facet.showMatchTypeSelector ?? true,
      negotiatorRequestString: facet.negotiatorRequestString,
      builtIn:
        facet.builtIn ||
        false /** if this filter should be ignored for dropdown filters generation */,
      showFacet: facet.showFacet || false,
      adaptive:
        facet.adaptive ||
        false /** if the filters options should react on search results */,
      sortColumn:
        facet.sortColumn || "name" /** specify a column to apply sorting on */,
      sortDirection:
        (facet.sortDirection?.toLowerCase() as SortDirection) || "asc",
    };
  }
  return filterDetails;
}

export function createFiltersSafely(filterFacets: IFilterFacet[]): {
  filterDetails: Record<string, IFilterDetails>;
  warnings: string[];
} {
  const warnings: string[] = [];
  const filterDetails: Record<string, IFilterDetails> = {};

  for (const facet of filterFacets) {
    const facetValidationErrors = validateFilterFacets([facet]);
    const facetLabel = facet.facetTitle || facet.facetIdentifier || "unknown";

    if (facetValidationErrors.length > 0) {
      warnings.push(
        `Skipped filter '${facetLabel}': ${facetValidationErrors.join(" ")}`
      );
      continue;
    }

    const facetId = getFacetIdentifier(facet);
    if (!facetId) {
      warnings.push(`Skipped filter '${facetLabel}': missing facet identifier.`);
      continue;
    }

    if (filterDetails[facetId]) {
      warnings.push(
        `Skipped filter '${facetLabel}': duplicate facet identifier '${facetId}'.`
      );
      continue;
    }

    try {
      Object.assign(filterDetails, createFilters([facet]));
    } catch (error: unknown) {
      warnings.push(
        `Skipped filter '${facetLabel}': ${
          error instanceof Error ? error.message : "unexpected error"
        }`
      );
    }
  }

  return { filterDetails, warnings };
}

function getFilterOptions(filterFacet: IFilterFacet) {
  if (filterFacet.customOptions && filterFacet.customOptions.length > 0) {
    return customFilterOptions(filterFacet);
  } else if (filterFacet.component === "OntologyFilter") {
    return ontologyFilterOptions(filterFacet);
  } else if (filterFacet.optionsFromGroupBy) {
    return groupByFilterOptions(filterFacet);
  } else {
    return genericFilterOptions(filterFacet);
  }
}
